import { displayMessage } from '../helpers/helper.js'
import { i18n } from '@/i18n'
import axios from 'axios'
import { serializeParams } from '@/helpers/queryParams'

const DEFAULT_SORT_BY = 'created_at'

/**
 * Service for fetching refunds through the refunds REST API.
 * Owns its axios instance (base URL, bearer auth, automatic token refresh), mirroring
 * ReturnSettingsService — the OMS direction is one axios client per service rather than Amplify.
 */
export class RefundService {
  /** @type {Object} Authentication provider ($auth). */
  auth
  /** @type {string} Current access token. */
  accessToken
  /** @type {Promise<void>|null} Tracks an in-flight token refresh. */
  tokenRefreshing
  /** @type {import('axios').AxiosInstance} */
  refundAPI

  /**
   * @param {Object} auth - The authentication provider ($auth: accessToken, expiresAt, isAuthenticated()).
   */
  constructor(auth) {
    this.auth = auth
    this.accessToken = auth?.accessToken
    this.tokenRefreshing = null

    if (!auth?.accessToken) {
      this.emitError('missing_auth', true)
    }

    this.refundAPI = axios.create({
      baseURL: process.env.VUE_APP_RETURN_API_URL,
      timeout: 29900,
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${this.accessToken}`,
      },
      paramsSerializer: serializeParams,
    })

    // Request interceptor: refresh the token before it expires.
    this.refundAPI.interceptors.request.use(
      (config) => this.refreshTokenIfNeeded(config),
      (error) => Promise.reject(error)
    )

    // Response interceptor: list() surfaces a domain-specific message, so we only log and rethrow
    // here (no snackbar) to avoid a duplicate, generic error message.
    this.refundAPI.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('Refund API:', error)
        return Promise.reject(error)
      }
    )
  }

  /**
   * Refreshes the access token if it is expired or about to expire (30s buffer) before a request.
   *
   * @private
   * @param {import('axios').AxiosRequestConfig} config
   * @returns {Promise<import('axios').AxiosRequestConfig>}
   */
  async refreshTokenIfNeeded(config) {
    const now = Date.now()
    if (this.auth.expiresAt - 30000 > now) {
      return config
    }
    if (!this.tokenRefreshing) {
      this.tokenRefreshing = this.refreshToken()
    }
    await this.tokenRefreshing
    config.headers.Authorization = `Bearer ${this.accessToken}`
    return config
  }

  /**
   * Refreshes the token through the auth provider and updates the instance default header.
   *
   * @private
   * @returns {Promise<void>}
   */
  async refreshToken() {
    try {
      await this.auth.isAuthenticated()
      this.accessToken = this.auth.accessToken
      this.refundAPI.defaults.headers.Authorization = `Bearer ${this.accessToken}`
    } finally {
      this.tokenRefreshing = null
    }
  }

  /**
   * Displays a localized error and optionally throws.
   *
   * @private
   * @param {string} messageKey - Key looked up under the `refund.` namespace, falling back to the raw value.
   * @param {boolean} [withThrow=false]
   */
  emitError(messageKey, withThrow = false) {
    const text = i18n.te(`refund.${messageKey}`) ? i18n.t(`refund.${messageKey}`) : messageKey
    displayMessage(text)
    if (withThrow) {
      throw new Error(text)
    }
  }

  /**
   * Retrieves a paginated list of refunds.
   * Displays an error message if the request fails.
   *
   * @param {Object} options - Vuetify data-table options (page, itemsPerPage, sortBy, sortDesc).
   * @param {Array<Object>} [filters=[]] - Advanced filters from the FilterDialog.
   * @param {Object} [quickFilters={}] - Quick-filter fields (order_id, refund_id).
   * @param {boolean} [pagination=true] - Whether to apply pagination.
   * @param {?string} [locationId=null] - When set, restricts results to this store's `source_location_id`
   *   (store view). The constraint is forwarded to the back-end so it cannot be bypassed client-side.
   * @returns {Promise<{data: Object[], nbRefunds: number}|undefined>} The refunds and total count, or undefined on error.
   */
  async list(options = {}, filters = [], quickFilters = {}, pagination = true, locationId = null) {
    try {
      const params = this.buildParams(options, filters, quickFilters, pagination, locationId)
      const response = await this.refundAPI.get('/v1/int/refunds', { params })
      return { data: response.data.data, nbRefunds: response.data.nbRefunds }
    } catch {
      displayMessage(i18n.t('refund.refundsLoadingError'), 'error')
    }
  }

  /**
   * Retrieves the full detail of a single refund (header, articles, fees,
   * transactions and customer). Displays an error message if the request fails.
   *
   * @param {string} id - The refund reference.
   * @returns {Promise<Object|undefined>} The refund detail, or undefined on error / not found.
   */
  async get(id) {
    try {
      const response = await this.refundAPI.get(`/v1/int/refunds/${encodeURIComponent(id)}`)
      return response.data
    } catch (error) {
      // 404 → genuinely not found: return null so the caller renders the "not found" state.
      // Any other failure (5xx, network, timeout) is re-thrown so the caller can show a distinct
      // error/retry state instead of masking a transient failure as "refund does not exist".
      if (error.response?.status === 404) {
        return null
      }
      throw error
    }
  }

  /**
   * Updates a refund: changes its status and/or appends/updates transaction(s).
   * Maps to PATCH /v1/int/refunds/{id} (RefundUpdateBody). Displays an error
   * message and rethrows if the request fails, so the caller can skip the
   * optimistic UI update.
   *
   * @param {string} id - The refund reference (refund_id).
   * @param {{current_status?: string, status_comment?: string, transactions?: Object[]}} payload - The partial update.
   * @returns {Promise<Object>} The updated refund (RefundBody).
   */
  async update(id, payload) {
    try {
      const response = await this.refundAPI.patch(`/v1/int/refunds/${encodeURIComponent(id)}`, payload)
      return response.data
    } catch (error) {
      displayMessage(i18n.t('refund.refundUpdateError'), 'error')
      throw error
    }
  }

  /**
   * Builds the query-parameter object for the refunds list request.
   *
   * @param {Object} [options={}] - Vuetify data-table options.
   * @param {Array<Object>} [originalFilters=[]] - Advanced filters from the FilterDialog.
   * @param {Object} [originalQuickFilters={}] - Quick-filter fields (order_id, refund_id).
   * @param {boolean} [pagination=true] - Whether to include pagination parameters.
   * @param {?string} [locationId=null] - When set, forces the store scope (`source_location_id`) query param,
   *   overriding any user-supplied source_location_id filter (store view, RTLOMS-543).
   * @returns {Object} The query parameters.
   */
  buildParams(options = {}, originalFilters = [], originalQuickFilters = {}, pagination = true, locationId = null) {
    const { page, itemsPerPage, sortDesc, sortBy } = options
    let filters = structuredClone(originalFilters)
    let quickFilters = structuredClone(originalQuickFilters)
    const params = {
      // The API expects scalars (sortBy concatenated as a field name, sortDesc compared to "true").
      // Vuetify gives single-element arrays when a column is sorted, so send the first element —
      // never the array — to keep the param type consistent with the unsorted default.
      sortBy: sortBy?.length ? sortBy[0] : DEFAULT_SORT_BY,
      sortDesc: sortDesc?.length ? sortDesc[0] : true,
      itemsPerPage: false,
    }
    if (pagination) {
      params.page = page
      params.itemsPerPage = itemsPerPage ?? false
    }
    const filterAsQueryString = ['current_status', 'source_location_id']
    for (const filterName of filterAsQueryString) {
      const filter = filters.find((filter) => filter.active && filter.name === filterName)
      if (filter) {
        params[filterName] = filter.value
        filters = filters.filter((f) => f.name !== filterName)
      }
    }
    // Only refund_id is promoted to a dedicated query param (matched partially by the API there).
    // order_id and return_id are intentionally NOT promoted: as dedicated query params the API matches
    // them exactly, whereas routing them through `filters` (below) yields a partial, case-insensitive
    // match — consistent with the returns list, which is what we want for these columns.
    const quickFilterAsQueryString = ['refund_id']
    for (const quickFilterName of quickFilterAsQueryString) {
      if (quickFilters?.[quickFilterName]) {
        params[quickFilterName] = quickFilters[quickFilterName]
        delete quickFilters[quickFilterName]
      }
    }

    // Store view (RTLOMS-543): the forced store scope always wins over any user-supplied
    // source_location_id filter, so the store restriction cannot be bypassed client-side.
    if (locationId) {
      params.source_location_id = locationId
    }

    const builtFilters = this.buildFilters(filters, quickFilters)
    if (Object.entries(builtFilters).length !== 0) {
      params.filters = JSON.stringify(builtFilters)
    }

    return params
  }

  /**
   * Builds the filters object from the advanced filters (excluding status and
   * source_location_id, both sent as dedicated query params) plus any leftover quick filters.
   * The `order_id` quick filter flows through here (not as a dedicated query param) so the API
   * matches it partially, like the returns list. `refund_id` is the dedicated partial query param.
   *
   * @param {Array<Object>} filters - Advanced filters from the FilterDialog.
   * @param {Object} quickFilters - Leftover quick-filter fields (e.g. order_id).
   * @returns {Object} A plain object of active filters.
   */
  buildFilters(filters, quickFilters) {
    const built = {}
    for (let filter of filters) {
      if (filter.active) {
        built[filter.name] = filter.value
      }
    }
    const quickFiltersKeys = Object.keys(quickFilters)
    for (const key of quickFiltersKeys) {
      if (quickFilters[key]) {
        built[key] = quickFilters[key]
      }
    }
    return built
  }
}
